/**
 * DatasetExplorer — Inteligência de Dados (Fase 3)
 * -----------------------------------------------------------------------------
 * Explorador dos registros importados de sistemas externos. Monta a grade
 * dinamicamente a partir do dicionário de campos descoberto na importação
 * (`external_dataset_fields`), com busca normalizada, filtros por campo,
 * ordenação, paginação server-side e exportação CSV da página atual.
 *
 * Performance:
 *   - Nenhum dado é carregado por inteiro: tudo passa por RPC paginada.
 *   - Filtros/ordenação priorizam campos com índice de expressão ligado.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-23 v1.1.0 - Botão/modal "Comparar" (cruzamento com a base interna).
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Fase 3 - Explorador de Dados).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowUpDown,
  Database,
  Download,
  Filter,
  Gauge,
  Rocket,
  GitCompareArrows,
  Layers,
  Plus,
  Search,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  ListPageFiltersBar,
  ListPageContent,
  ListPageFooter,
  KpiCard,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  useExternalDatasets,
  useExternalDatasetFields,
  type ExternalDatasetField,
} from '@/hooks/data/useExternalDatasets';
import {
  useDatasetRecords,
  useDatasetOverview,
  getPayloadValue,
  type DatasetFilter,
  type DatasetFilterOp,
  type DatasetRecord,
} from '@/hooks/data/useDatasetRecords';
import { DatasetCompareDialog } from '@/components/data/DatasetCompareDialog';
import { DatasetActionDialog } from '@/components/data/DatasetActionDialog';

/** Hook utilitário simples de debounce (evita disparo a cada tecla). */
function useDebouncedValue<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Tamanho fixo da página (server-side) */
const PAGE_SIZE = 50;

/** Operadores oferecidos por tipo de campo */
const OPS_BY_TYPE: Record<string, { value: DatasetFilterOp; label: string }[]> = {
  text: [
    { value: 'contains', label: 'Contém' },
    { value: 'eq', label: 'Igual a' },
    { value: 'neq', label: 'Diferente de' },
    { value: 'starts_with', label: 'Começa com' },
    { value: 'empty', label: 'Vazio' },
    { value: 'not_empty', label: 'Preenchido' },
  ],
  number: [
    { value: 'eq', label: 'Igual a' },
    { value: 'gt', label: 'Maior que' },
    { value: 'gte', label: 'Maior ou igual' },
    { value: 'lt', label: 'Menor que' },
    { value: 'lte', label: 'Menor ou igual' },
    { value: 'empty', label: 'Vazio' },
    { value: 'not_empty', label: 'Preenchido' },
  ],
  date: [
    { value: 'gte', label: 'A partir de' },
    { value: 'lte', label: 'Até' },
    { value: 'eq', label: 'Exatamente em' },
    { value: 'empty', label: 'Vazio' },
    { value: 'not_empty', label: 'Preenchido' },
  ],
  boolean: [
    { value: 'eq', label: 'Igual a' },
    { value: 'empty', label: 'Vazio' },
    { value: 'not_empty', label: 'Preenchido' },
  ],
};

/** Formata data/hora no padrão pt-BR */
const formatDateTime = (value: string | null) =>
  value ? format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : '—';

/** Escapa um valor para célula CSV */
const toCsvCell = (value: string) => `"${(value ?? '').replace(/"/g, '""')}"`;

/** Página "Explorador" do módulo Inteligência de Dados */
export default function DatasetExplorer() {
  const navigate = useNavigate();
  const { datasetId: routeDatasetId } = useParams<{ datasetId?: string }>();

  const { datasets, isLoading: loadingDatasets } = useExternalDatasets();
  const [datasetId, setDatasetId] = useState<string | null>(routeDatasetId ?? null);

  // Busca livre (debounce evita disparo por tecla)
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 400);

  // Filtros, ordenação e paginação
  const [filters, setFilters] = useState<DatasetFilter[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortPath, setSortPath] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [detail, setDetail] = useState<DatasetRecord | null>(null);
  const [onlyIndexed, setOnlyIndexed] = useState(false);

  /** Seleciona automaticamente o primeiro dataset com dados */
  useEffect(() => {
    if (datasetId || datasets.length === 0) return;
    const preferred = datasets.find((d) => Number(d.record_count) > 0) ?? datasets[0];
    setDatasetId(preferred.id);
  }, [datasets, datasetId]);

  /** Mantém a URL sincronizada (permite compartilhar o link do dataset) */
  useEffect(() => {
    if (datasetId && datasetId !== routeDatasetId) {
      navigate(`/tenant/data/explorer/${datasetId}`, { replace: true });
    }
  }, [datasetId, routeDatasetId, navigate]);

  const { overview } = useDatasetOverview(datasetId);
  const { fields } = useExternalDatasetFields(datasetId);

  const { records, totalCount, isLoading, isFetching, error } = useDatasetRecords({
    datasetId,
    search,
    filters,
    sortPath,
    sortDir,
    page,
    pageSize: PAGE_SIZE,
  });

  /** Colunas visíveis da grade (limitadas para não estourar a largura) */
  const columns = useMemo<ExternalDatasetField[]>(() => {
    const visible = fields.filter((f) => f.is_visible);
    const source = onlyIndexed ? visible.filter((f) => f.is_indexed) : visible;
    return source.slice(0, 12);
  }, [fields, onlyIndexed]);

  /** Qualquer mudança de contexto volta para a primeira página */
  useEffect(() => {
    setPage(0);
  }, [datasetId, search, filters, sortPath, sortDir]);

  /** Alterna ordenação ao clicar no cabeçalho da coluna */
  const toggleSort = (path: string) => {
    if (sortPath === path) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortPath(path);
    setSortDir('asc');
  };

  /** Adiciona uma linha de filtro vazia */
  const addFilter = () => {
    const first = fields[0];
    if (!first) return;
    setFilters((prev) => [...prev, { path: first.json_path, op: 'contains', value: '' }]);
  };

  /** Atualiza uma linha de filtro existente */
  const updateFilter = (index: number, patch: Partial<DatasetFilter>) => {
    setFilters((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  };

  /** Remove uma linha de filtro */
  const removeFilter = (index: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  };

  /** Exporta a página atual em CSV (evita cargas gigantes no navegador) */
  const exportCsv = () => {
    if (records.length === 0 || columns.length === 0) return;

    const header = columns.map((c) => toCsvCell(c.label)).join(';');
    const lines = records.map((record) =>
      columns.map((c) => toCsvCell(getPayloadValue(record.payload, c.json_path))).join(';'),
    );
    const csv = [header, ...lines].join('\n');

    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${overview?.name ?? 'dataset'}-pagina-${page + 1}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Modal de comparação com clientes/contatos/veículos (Fase 3 - Comparar)
  const [compareOpen, setCompareOpen] = useState(false);
  // Modal de ações em lote sobre a seleção atual (Fase 4 - Ações)
  const [actionOpen, setActionOpen] = useState(false);

  const activeFiltersCount = filters.filter(
    (f) => f.op === 'empty' || f.op === 'not_empty' || (f.value ?? '') !== '',
  ).length;
  const indexedCount = fields.filter((f) => f.is_indexed).length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Explorador de dados"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="xs"
              className="gap-1.5"
              onClick={() => navigate('/tenant/data/datasets')}
            >
              <ArrowLeft />
              Datasets
            </Button>
            <Button
              variant="outline"
              size="xs"
              className="gap-1.5"
              disabled={!datasetId || fields.length === 0}
              onClick={() => setCompareOpen(true)}
            >
              <GitCompareArrows />
              Comparar
            </Button>
            {/* Fase 4 — cria um lote de ação com a seleção atual (filtros + busca) */}
            <Button
              variant="outline"
              size="xs"
              className="gap-1.5"
              disabled={!datasetId || totalCount === 0}
              onClick={() => setActionOpen(true)}
            >
              <Rocket />
              Ações
            </Button>
            <Button
              variant="outline"
              size="xs"
              className="gap-1.5"
              disabled={records.length === 0}
              onClick={exportCsv}
            >
              <Download />
              Exportar página
            </Button>
          </div>
        }
      />

      <ListPageKpis className="border-b-0 pb-1">
        <KpiCard
          color="blue"
          icon={Database}
          label="Registros encontrados"
          value={totalCount.toLocaleString('pt-BR')}
        />
        <KpiCard
          color="purple"
          icon={Layers}
          label="Total no dataset"
          value={Number(overview?.record_count ?? 0).toLocaleString('pt-BR')}
        />
        <KpiCard color="cyan" icon={Gauge} label="Campos mapeados" value={fields.length} />
        <KpiCard color="green" icon={Zap} label="Campos indexados" value={indexedCount} />
      </ListPageKpis>

      <ListPageFiltersBar className="border-b-0 bg-transparent">
        <Select value={datasetId ?? ''} onValueChange={(v) => setDatasetId(v)}>
          <SelectTrigger className="w-full sm:w-[260px]">
            <SelectValue placeholder="Selecione um dataset" />
          </SelectTrigger>
          <SelectContent>
            {datasets.map((dataset) => (
              <SelectItem key={dataset.id} value={dataset.id}>
                {dataset.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            maxLength={100}
            placeholder="Buscar em todo o registro..."
            className="pl-8"
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <Button variant="outline" className="gap-1.5" onClick={() => setFiltersOpen(true)}>
          <Filter className="h-4 w-4" />
          Filtros
          {activeFiltersCount > 0 && <Badge variant="secondary">{activeFiltersCount}</Badge>}
        </Button>

        <div className="flex items-center gap-2">
          <Switch id="only-indexed" checked={onlyIndexed} onCheckedChange={setOnlyIndexed} />
          <label htmlFor="only-indexed" className="text-xs text-muted-foreground">
            Só campos indexados
          </label>
        </div>
      </ListPageFiltersBar>

      <ListPageContent>
        {error && (
          <InlineNotice variant="error" className="mb-3">
            {error.message}
          </InlineNotice>
        )}

        {!datasetId && !loadingDatasets ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Database className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhum dataset disponível</p>
            <p className="text-xs text-muted-foreground">
              Configure um dataset e faça a primeira importação para explorar os dados.
            </p>
            <Button
              variant="outline"
              className="mt-2 gap-1.5"
              onClick={() => navigate('/tenant/data/datasets')}
            >
              <Plus className="h-4 w-4" />
              Configurar dataset
            </Button>
          </div>
        ) : isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : columns.length === 0 ? (
          <InlineNotice variant="info">
            Este dataset ainda não possui campos mapeados. Rode uma importação para que o sistema
            descubra automaticamente a estrutura do JSON.
          </InlineNotice>
        ) : records.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Search className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhum registro encontrado</p>
            <p className="text-xs text-muted-foreground">
              Ajuste a busca ou os filtros aplicados.
            </p>
          </div>
        ) : (
          <div className="overflow-auto rounded-lg border">
            <Table className="min-w-[900px]">
              <TableHeader>
                <TableRow>
                  {columns.map((column) => (
                    <TableHead key={column.id} className="whitespace-nowrap">
                      <button
                        type="button"
                        className="flex items-center gap-1 text-left hover:text-foreground"
                        onClick={() => toggleSort(column.json_path)}
                      >
                        {column.label}
                        {column.is_indexed && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Zap className="h-3 w-3 text-emerald-500" />
                            </TooltipTrigger>
                            <TooltipContent>Campo indexado (filtro rápido)</TooltipContent>
                          </Tooltip>
                        )}
                        <ArrowUpDown
                          className={
                            sortPath === column.json_path
                              ? 'h-3 w-3 text-foreground'
                              : 'h-3 w-3 text-muted-foreground/50'
                          }
                        />
                      </button>
                    </TableHead>
                  ))}
                  <TableHead className="whitespace-nowrap">Importado em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => (
                  <TableRow
                    key={record.id}
                    className="cursor-pointer"
                    onClick={() => setDetail(record)}
                  >
                    {columns.map((column) => (
                      <TableCell key={column.id} className="max-w-[240px] truncate text-sm">
                        {getPayloadValue(record.payload, column.json_path) || '—'}
                      </TableCell>
                    ))}
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {formatDateTime(record.imported_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </ListPageContent>

      <ListPageFooter>
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-xs text-muted-foreground">
            {isFetching ? 'Atualizando...' : `Página ${page + 1} de ${totalPages}`}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      </ListPageFooter>

      {/* Filtros avançados (padrão do sistema: Sheet à direita) */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Filtros do dataset</SheetTitle>
            <SheetDescription>
              Filtre por qualquer campo mapeado. Campos indexados respondem mais rápido.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-3">
            {filters.length === 0 && (
              <InlineNotice variant="info">Nenhum filtro aplicado.</InlineNotice>
            )}

            {filters.map((filter, index) => {
              const field = fields.find((f) => f.json_path === filter.path);
              const ops = OPS_BY_TYPE[field?.data_type ?? 'text'] ?? OPS_BY_TYPE.text;
              const needsValue = filter.op !== 'empty' && filter.op !== 'not_empty';

              return (
                <div key={`${filter.path}-${index}`} className="space-y-2 rounded-lg border p-3">
                  <div className="flex items-center gap-2">
                    <Select
                      value={filter.path}
                      onValueChange={(v) => updateFilter(index, { path: v, value: '' })}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {fields.map((f) => (
                          <SelectItem key={f.id} value={f.json_path}>
                            {f.label}
                            {f.is_indexed ? ' ⚡' : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" onClick={() => removeFilter(index)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <Select
                    value={filter.op}
                    onValueChange={(v) => updateFilter(index, { op: v as DatasetFilterOp })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ops.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {needsValue && (
                    <Input
                      value={filter.value ?? ''}
                      maxLength={120}
                      type={field?.data_type === 'date' ? 'date' : 'text'}
                      placeholder="Valor"
                      onChange={(e) => updateFilter(index, { value: e.target.value })}
                    />
                  )}
                </div>
              );
            })}

            <div className="flex items-center gap-2">
              <Button variant="outline" className="flex-1 gap-1.5" onClick={addFilter}>
                <Plus className="h-4 w-4" />
                Adicionar filtro
              </Button>
              {filters.length > 0 && (
                <Button variant="ghost" className="gap-1.5" onClick={() => setFilters([])}>
                  <X className="h-4 w-4" />
                  Limpar
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Detalhe do registro (payload completo) */}
      <Sheet open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Detalhes do registro</SheetTitle>
            <SheetDescription>
              Identificador externo: {detail?.external_id ?? '—'}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <span>Criado na origem: {formatDateTime(detail?.source_created_at ?? null)}</span>
              <span>Atualizado na origem: {formatDateTime(detail?.source_updated_at ?? null)}</span>
            </div>

            {/* Campos mapeados em formato legível */}
            <div className="space-y-1 rounded-lg border p-3">
              {fields.map((field) => (
                <div key={field.id} className="flex justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">{field.label}</span>
                  <span className="max-w-[60%] truncate text-right">
                    {getPayloadValue(detail?.payload, field.json_path) || '—'}
                  </span>
                </div>
              ))}
            </div>

            {/* Payload bruto (auditoria) */}
            <pre className="max-h-[40vh] overflow-auto rounded-lg bg-muted p-3 text-xs">
              {JSON.stringify(detail?.payload ?? {}, null, 2)}
            </pre>
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal de comparação com a base interna */}
      <DatasetCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        datasetId={datasetId}
        datasetName={overview?.name}
        fields={fields}
      />

      {/* Modal de ações em lote sobre a seleção atual (filtros + busca) */}
      <DatasetActionDialog
        open={actionOpen}
        onOpenChange={setActionOpen}
        datasetId={datasetId}
        datasetName={overview?.name}
        selection={{ mode: 'filter', search: search || null, filters }}
      />
    </ListPageLayout>
  );
}
